<script setup>
import { computed, resolveComponent } from 'vue'

const props = defineProps({
  // New props
  variant: { type: String, default: undefined },
  // Legacy: color maps to the same variant classes
  color: { type: String, default: undefined },

  // New sizes: sm / md / lg
  // Legacy sizes: small / big (mapped below)
  size: { type: String, default: undefined },

  pill: { type: Boolean, default: false },
  iconOnly: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  to: { type: [String, Object], default: undefined },
  type: { type: String, default: 'button' },
  fullWidth: { type: Boolean, default: false },
  fullWidthMobile: { type: Boolean, default: false },
  responsive: { type: Boolean, default: false },
  round: { type: Boolean, default: false },
  noBorderRadius: { type: Boolean, default: false },
  withBorder: { type: Boolean, default: false },
  transparent: { type: Boolean, default: false },
  styleless: { type: Boolean, default: false },
  // Legacy icon support
  icon: { type: String, default: undefined },
  iconPos: { type: String, default: 'left' },
})

const nuxtLink = resolveComponent('NuxtLink')
const tag = computed(() => (props.to ? nuxtLink : 'button'))

// Accept both new variant and legacy color prop
const activeVariant = computed(() => props.variant || props.color)

// Normalize size: map legacy 'big'→'lg', 'small'→'sm'
const activeSize = computed(() => {
  if (!props.size) return undefined
  if (props.size === 'big') return 'lg'
  if (props.size === 'small') return 'sm'
  return props.size
})
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :type="to ? undefined : type"
    :disabled="(!to && disabled) || undefined"
    :class="[
      $style['btn'],
      activeVariant && $style[`btn--${activeVariant}`],
      activeSize && $style[`btn--${activeSize}`],
      (pill || round) && $style['btn--pill'],
      (iconOnly || (!$slots.default && icon)) && $style['btn--icon-only'],
      loading && $style['btn--loading'],
      fullWidth && $style['btn--full-width'],
      fullWidthMobile && $style['btn--full-width-mobile'],
      responsive && $style['btn--responsive'],
      noBorderRadius && $style['btn--no-border-radius'],
      withBorder && $style['btn--with-border'],
      transparent && $style['btn--transparent'],
      styleless && $style['btn--styleless'],
    ]"
    :aria-busy="loading || undefined"
  >
    <span v-if="$slots.left || (icon && iconPos === 'left')" :class="[$style['btn__icon'], $style['btn__icon--left']]">
      <slot name="left" />
      <Icon v-if="!$slots.left && icon && iconPos === 'left'" :name="icon" />
    </span>
    <span :class="$style['btn__label']">
      <slot />
    </span>
    <span v-if="$slots.right || (icon && iconPos === 'right')" :class="[$style['btn__icon'], $style['btn__icon--right']]">
      <slot name="right" />
      <Icon v-if="!$slots.right && icon && iconPos === 'right'" :name="icon" />
    </span>
  </component>
</template>

<style module>
.btn {
  --background-color: var(--opacity-surface);
  --color: var(--text-base);
  --padding-y: var(--spacing-l);
  --padding-x: var(--spacing-xl);
  --icon-size: var(--spacing-xl);

  background: var(--background-color);
  color: var(--color);
  border-radius: 999px;
  font-weight: var(--font-weight-bold);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing);
  border: none;
  cursor: pointer;
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  line-height: 1;
  vertical-align: middle;
  position: relative;
  padding: var(--padding-y) var(--padding-x);
  transition: all var(--duration-micro-turbo) var(--easing-transition);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-shadow: 1px 1px 1px rgb(0 0 0 / 25%);
  font-style: normal;
}

/* The public pages: amber calls to action in black capitals, like the homepage */
:global(:where([data-theme="stage"])) .btn {
  text-shadow: none;
}

.btn:hover {
  --background-color: var(--opacity-surface-state-1);
}

.btn:active {
  transform: scale(0.95);
}

.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--color);
}

.btn[disabled],
.btn--loading {
  pointer-events: none;
  cursor: not-allowed;
}

.btn[disabled] {
  opacity: 0.5;
}

.btn--loading {
  opacity: 0.75;
}

.btn--cta {
  --background-color: var(--cta-gradient);
  --color: #fff;
  background: var(--cta-gradient);
  color: #fff;
}

:global(:where([data-theme="stage"])) .btn--cta {
  --color: var(--stage-black);
  color: var(--stage-black);
}

.btn--cta:hover {
  --background-color: var(--cta-gradient);
  background: var(--cta-gradient);
  filter: brightness(1.1);
}

.btn--primary {
  --background-color: var(--primary-color);
  --color: var(--dark-color-10);
  background: var(--primary-color);
  color: var(--dark-color-10);
}

.btn--primary:hover {
  background: var(--primary-color);
  filter: brightness(1.1);
}

:global(:where([data-theme="stage"])) :is(.btn--cta, .btn--primary) {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.btn--secondary {
  box-shadow: inset 0 0 0 1px var(--border-subtle);
}

.btn--outline {
  background: transparent;
  box-shadow: inset 0 0 0 1px var(--border-subtle);
}

.btn--outline:hover {
  background: var(--opacity-surface);
}

.btn--ghost {
  background: transparent;
}

.btn--ghost:hover {
  background: var(--opacity-surface);
}

.btn--danger {
  background: oklch(60% 0.22 28deg / 20%);
  color: oklch(70% 0.2 28deg);
}

.btn--danger:hover {
  background: oklch(60% 0.22 28deg / 30%);
}

.btn--opacity {
  background: var(--opacity-surface);
}

.btn--sm {
  --padding-y: var(--spacing);
  --padding-x: var(--spacing-m);
  --icon-size: var(--spacing-l);
  font-size: var(--font-size-small);
}

.btn--lg {
  --padding-y: var(--spacing-xl);
  --padding-x: var(--spacing-xxl);
  font-size: var(--font-size-large);
}

.btn--pill {
  border-radius: 999px;
}

.btn--no-border-radius {
  border-radius: 0;
}

.btn--with-border {
  box-shadow: inset 0 0 0 1px var(--border-subtle);
}

.btn--transparent {
  background: transparent;
}

.btn--styleless {
  background: transparent;
  border-radius: 0;
  font-family: var(--font-family);
  text-transform: none;
}

.btn__icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.btn__icon--left {
  margin-right: var(--spacing);
}

.btn__icon--right {
  margin-left: var(--spacing);
}

.btn__icon svg {
  margin: 0 calc(var(--spacing-s) * -1);
  width: var(--icon-size);
  height: var(--icon-size);
  stroke-width: 1.5;
}

.btn__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.btn--icon-only {
  padding: var(--spacing-l);
  aspect-ratio: 1;
}

.btn--icon-only .btn__icon {
  margin: 0;
}

.btn--icon-only.btn--sm {
  padding: var(--spacing-s);
}

.btn--icon-only.btn--lg {
  padding: var(--spacing-xl);
}

.btn--icon-only .btn__label {
  display: none;
}

.btn--full-width {
  width: 100%;
}

@media (width < 50rem) {
  .btn--full-width-mobile {
    width: 100%;
  }
}

@media (width < 50rem) {
  .btn--responsive {
    border-radius: 100%;
    padding: 0;
    width: var(--spacing-xxxl);
    height: var(--spacing-xxxl);
  }

  .btn--responsive .btn__label {
    display: none;
  }

  .btn--responsive .btn__icon {
    margin: 0;
  }
}
</style>
